/* eslint-disable camelcase */
import { type OverridedMixpanel } from 'mixpanel-browser';
import { getBaseProfileProperties, getBaseSuperProperties } from '@app/actions/mixpanel_properties';
import {
  fromMobileApp, getDataFromStorage, saveDataToStorage, removeDataFromStorage
} from '@common/config/utils';
import { getUA } from 'react-device-detect';
import { log, LOG_LEVELS } from '@common/config/app_logger';
import { analyticsService } from '.';
import { AnalyticsProvider } from './AnalyticsProvider';
import { type Callback, EventEmitter } from './EventEmitter';

export class MixpanelProvider extends AnalyticsProvider {
  private instance?: OverridedMixpanel;

  private excludedEvents: string[];

  private emitter: EventEmitter = new EventEmitter();

  public get name(): string { return 'Mixpanel'; }

  constructor() {
    super();
    this.excludedEvents = [
      'first purchase',
      'spent credits',
      'achievement unlocked',
      'first visit',
      'chat ended',
      'voice ended',
      'vc ended',
      'inner page view'
    ];

    if (!window.config.advisorProfileViewMpEvent) {
      this.excludedEvents.push('advisor profile view');
    }
  }

  public getInstance = (callback: (instance: OverridedMixpanel) => void) => {
    if (this.instance) {
      callback(this.instance);
      return;
    }
    navigator.locks.request('mixpanelInstance', async () => {
      if (!this.instance) {
        this.instance = await import('mixpanel-browser').then(module => module.default);
      }
      callback(this.instance);
    });
  };

  private validate() {
    this.getInstance((mixpanel) => {
      const userIdProp = mixpanel.get_property('user id');
      if (
        (!window.currentUserId && userIdProp)
        || (window.currentUserId && userIdProp && window.currentUserId !== userIdProp)
      ) this.reset();
    });
  }

  private ignoreTrackSessionBeganEvent(lastHitSec: number) {
    if (getUA.includes('HeadlessChrome') || fromMobileApp) return true;
    if (lastHitSec && lastHitSec / 60 < parseInt(window.config.sessionBeganTimer, 10)) return true;
    return false;
  }

  private trackSessionBeganEvent(lastHitSec: number, sessionCount: number) {
    const properties = {
      'sessions count': sessionCount,
      'time from last session': lastHitSec
    };
    analyticsService.trackEvent('session began', properties);
  }

  private ignoreBySessionParamLogic() {
    const { reportSessionBegan } = window.config;
    if (reportSessionBegan === 'none') return true;
    if (reportSessionBegan === 'user' && !window.currentUserId) return true;
    const { dynamicConfig } = window.store.getState().application;
    if (reportSessionBegan === 'attributed'
      && (dynamicConfig?.analytics?.media_status === 'Organic'
        || window.currentUserId === undefined)) return true;
    return false;
  }

  private startNewSession() {
    const lastHit = getDataFromStorage('LAST_HIT');
    const lastHitSec = lastHit ? (new Date().getTime() - new Date(lastHit).getTime()) / 1000 : null;
    if (this.ignoreTrackSessionBeganEvent(lastHitSec)) return;
    removeDataFromStorage('advisor_urgency_signal');
    removeDataFromStorage('advisor_urgency_signal_text');
    saveDataToStorage('LAST_HIT', new Date());
    if (this.ignoreBySessionParamLogic()) return;
    const sessionCount = parseInt(getDataFromStorage('BROWSER_SESSIONS'), 10) || 0;
    this.trackSessionBeganEvent(lastHitSec || 0, sessionCount);
    saveDataToStorage('BROWSER_SESSIONS', sessionCount + 1);
  }

  public mapAndExtendProperties = (eventName: string, eventProperties?: Record<string, any>) => {
    if (this.excludedEvents.includes(eventName)) return eventProperties;

    const { user: { user } } = window.store.getState();

    const mergedProperties: Record<string, any> = {
      ...getBaseSuperProperties({ user, mixpanel: this.instance }),
      ...eventProperties
    };

    // Ensure 'login type' always has underscores replaced with spaces,
    // even if it comes from eventProperties (e.g., from userAnalytics or dynamicConfig)
    if (mergedProperties['login type'] && typeof mergedProperties['login type'] === 'string') {
      mergedProperties['login type'] = mergedProperties['login type']?.replace('_', ' ');
    }

    return mergedProperties;
  };

  public get initialized() { return !!this.instance; }

  public init(callback: Callback) {
    if (this.initialized) return;
    const subscription = this.emitter.subscribe('initialized', () => { callback(); subscription.unsubscribe(); });

    this.getInstance((mixpanel) => {
      const { mpToken, mpProxyUrl, mp_record_sessions_percent } = window.config;
      mixpanel.init(mpToken, {
        loaded: this.validate.bind(this),
        ignore_dnt: true,
        record_sessions_percent: mp_record_sessions_percent,
        api_host: mpProxyUrl,
        record_heatmap_data: true
      });
      this.emitter.emit('initialized');
      if (!window.currentUserId) {
        this.startNewSession();
      }
    });
  }

  public authorize(userId: string, params: Record<string, any>) {
    if (!this.initialized) {
      const subscription = this.emitter.subscribe('initialized', () => { this.authorize(userId, params); subscription.unsubscribe(); });
      return;
    }
    this.getInstance((mixpanel) => {
      if (!params.mixpanelAliasCreated) {
        // eslint-disable-next-line no-console
        console.log('🚀 ~ MixpanelProvider ~ authorize ~ mixpanel.alias:', userId);
        mixpanel.alias(userId);
        params.onAliasCreated?.();
      }
      mixpanel.identify(userId);
      mixpanel.register({ 'user id': Number(userId) });
      this.startNewSession();
    });
  }

  public reset() {
    this.getInstance((mixpanel) => {
      mixpanel.reset();
    });
  }

  public trackEvent(eventName: string, eventProperties?: Record<string, any>) {
    if (this.excludedEvents.includes(eventName)) return;

    log(LOG_LEVELS.INFO, 'Mixpanel', `track event: ${ eventName }`);
    log(LOG_LEVELS.DEBUG, 'Mixpanel', eventProperties);

    this.getInstance((mixpanel) => {
      mixpanel.people.set(getBaseProfileProperties());

      mixpanel.track(eventName, eventProperties);
    });
  }

  public trackPageVisit() { }
}
